import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Minus, Plus, Trash2, ShoppingBag, ArrowLeft, 
  Truck, ShieldCheck, CreditCard, Tag, X 
} from 'lucide-react';
import { useShopify } from '../context/ShopifyContext';
import { toast } from '../hooks/use-toast';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const CartPage = () => {
  const { 
    cart, 
    cartLoading, 
    updateCartLine, 
    removeFromCart, 
    checkout,
    clearCart 
  } = useShopify();

  const handleQuantityChange = async (lineId, newQuantity) => {
    if (newQuantity < 1) return;
    try {
      await updateCartLine(lineId, newQuantity);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update quantity.",
        variant: "destructive"
      });
    }
  };

  const handleRemove = async (lineId, productName) => {
    try {
      await removeFromCart(lineId);
      toast({
        title: "Removed from cart",
        description: `${productName} has been removed from your cart.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove item.",
        variant: "destructive"
      });
    }
  };

  const formatPrice = (price, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const handleCheckout = () => {
    if (cart?.checkoutUrl) {
      checkout();
    }
  };

  // Empty cart state
  if (!cart || cart.lines?.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, -5, 5, 0]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="inline-block mb-8"
            >
              <div className="relative">
                <ShoppingBag size={100} className="text-pink-200" />
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.5 }}
                  className="absolute -top-2 -right-2 w-8 h-8 bg-pink-500 rounded-full flex items-center justify-center"
                >
                  <span className="text-white text-sm font-bold">0</span>
                </motion.div>
              </div>
            </motion.div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Your Cart is Empty
            </h1>
            <p className="text-gray-600 mb-8 text-lg">
              Looks like you haven't added any pink treasures yet!
            </p>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/collections/all"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
              >
                <ShoppingBag size={20} />
                Start Shopping
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Shopping Cart
            </h1>
            <p className="text-gray-600 mt-1">
              {cart.totalQuantity} {cart.totalQuantity === 1 ? 'item' : 'items'} in your cart
            </p>
          </div>
          
          <Link
            to="/collections/all"
            className="flex items-center gap-2 text-pink-600 hover:text-pink-700 font-medium"
          >
            <ArrowLeft size={20} />
            Continue Shopping
          </Link>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            <AnimatePresence>
              {cart.lines.map((line, index) => (
                <motion.div
                  key={line.id}
                  layout
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20, height: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow p-4 md:p-6"
                >
                  <div className="flex gap-4 md:gap-6">
                    {/* Product Image */}
                    <Link 
                      to={`/products/${line.merchandise.productHandle}`}
                      className="flex-shrink-0"
                    >
                      <motion.div 
                        whileHover={{ scale: 1.05 }}
                        className="w-24 h-32 md:w-32 md:h-40 rounded-xl overflow-hidden bg-pink-50"
                      >
                        <img
                          src={line.merchandise.image?.url || line.merchandise.productImage?.url || 'https://placehold.co/200x250/fce7f3/be185d?text=No+Image'}
                          alt={line.merchandise.productTitle}
                          className="w-full h-full object-cover"
                        />
                      </motion.div>
                    </Link>

                    {/* Product Details */}
                    <div className="flex-1 flex flex-col justify-between">
                      <div>
                        <Link 
                          to={`/products/${line.merchandise.productHandle}`}
                          className="font-bold text-gray-900 text-lg hover:text-pink-600 transition-colors"
                        >
                          {line.merchandise.productTitle}
                        </Link>
                        
                        {/* Variant info */}
                        {line.merchandise.selectedOptions?.map((opt, idx) => (
                          <p key={idx} className="text-sm text-gray-500 mt-1">
                            {opt.name}: <span className="font-medium">{opt.value}</span>
                          </p>
                        ))}
                        
                        <p className="text-pink-600 font-bold text-lg mt-2">
                          {formatPrice(line.merchandise.price, line.merchandise.currencyCode)}
                        </p>
                      </div>

                      {/* Quantity & Actions */}
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center border-2 border-pink-200 rounded-xl overflow-hidden">
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleQuantityChange(line.id, line.quantity - 1)}
                              disabled={cartLoading || line.quantity <= 1}
                              className="p-2 hover:bg-pink-50 transition-colors disabled:opacity-50"
                            >
                              <Minus size={18} className="text-gray-600" />
                            </motion.button>
                            <span className="w-10 text-center font-semibold">{line.quantity}</span>
                            <motion.button
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleQuantityChange(line.id, line.quantity + 1)}
                              disabled={cartLoading}
                              className="p-2 hover:bg-pink-50 transition-colors disabled:opacity-50"
                            >
                              <Plus size={18} className="text-gray-600" />
                            </motion.button>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <p className="font-bold text-gray-900 hidden md:block">
                            {formatPrice(line.merchandise.price * line.quantity, line.merchandise.currencyCode)}
                          </p>
                          
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleRemove(line.id, line.merchandise.productTitle)}
                            disabled={cartLoading}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          >
                            <Trash2 size={20} />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Clear Cart */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={clearCart}
              className="w-full py-3 text-gray-500 hover:text-red-500 font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <Trash2 size={18} />
              Clear Cart
            </motion.button>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl shadow-lg p-6 sticky top-24"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>

              {/* Summary Lines */}
              <div className="space-y-4 pb-6 border-b border-pink-100">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-medium">{formatPrice(cart.subtotal, cart.currencyCode)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-green-600 font-medium">Calculated at checkout</span>
                </div>
                {cart.tax > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Estimated Tax</span>
                    <span className="font-medium">{formatPrice(cart.tax, cart.currencyCode)}</span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="flex justify-between py-6 border-b border-pink-100">
                <span className="text-xl font-bold text-gray-900">Total</span>
                <span className="text-xl font-bold text-pink-600">
                  {formatPrice(cart.total, cart.currencyCode)}
                </span>
              </div>

              {/* Checkout Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCheckout}
                disabled={cartLoading}
                className="w-full mt-6 py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
              >
                {cartLoading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard size={20} />
                    Proceed to Checkout
                  </>
                )}
              </motion.button>

              {/* Trust Badges */}
              <div className="mt-6 pt-6 border-t border-pink-100">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="p-2 bg-pink-50 rounded-lg">
                      <Truck size={20} className="text-pink-500" />
                    </div>
                    <span className="text-xs text-gray-600">Free Shipping<br/>Over $50</span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="p-2 bg-pink-50 rounded-lg">
                      <ShieldCheck size={20} className="text-pink-500" />
                    </div>
                    <span className="text-xs text-gray-600">Secure<br/>Checkout</span>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="mt-6 text-center">
                <p className="text-xs text-gray-500 mb-2">We accept</p>
                <div className="flex justify-center gap-2">
                  {['Visa', 'MC', 'Amex', 'PayPal'].map((method) => (
                    <div key={method} className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">
                      {method}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
